import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Package, Tag, ShoppingBag, Users,
  MessageSquare, Settings, ChevronLeft, ChevronRight,
  Zap, LogOut, X
} from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import { useAuth } from '../../context/AuthContext';

const navItems = [
  { icon: LayoutDashboard, key: 'dashboard',  path: '/admin',            label: null },
  { icon: Package,         key: 'products',   path: '/admin/products',   label: null },
  { icon: Tag,             key: 'categories', path: '/admin/categories', label: null },
  { icon: ShoppingBag,     key: 'orders',     path: '/admin/orders',     label: null },
  { icon: Users,           key: 'users',      path: '/admin/users',      label: null },
  { icon: Tag,             key: 'promotions', path: '/admin/promotions', label: 'Promotions' },
  { icon: MessageSquare,   key: 'contact',    path: '/admin/contact',    label: null },
  { icon: Settings,        key: 'settings',   path: '/admin/settings',   label: null },
];

export default function AdminSidebar({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) {
  const { t, lang } = useSettings();
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isRTL = lang === 'ar';

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <>
      {/* Overlay mobile */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 199,
            backdropFilter: 'blur(4px)',
          }}
        />
      )}

      <aside
        className={`admin-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'open' : ''}`}
        style={{
          background: 'var(--bg-secondary)',
          borderRight: isRTL ? 'none' : '1.5px solid var(--border)',
          borderLeft: isRTL ? '1.5px solid var(--border)' : 'none',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* ── Logo + boutons ── */}
        <div style={{
          padding: collapsed ? '20px 0' : '20px 16px',
          borderBottom: '1.5px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          minHeight: 72,
          gap: 8,
        }}>
          {!collapsed && (
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
              <div style={{ background: 'var(--accent)', borderRadius: 10, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Zap size={18} color="white" fill="white" />
              </div>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700 }}>
                Tech<span style={{ color: 'var(--accent)' }}>Store</span>
              </span>
            </Link>
          )}
          {collapsed && (
            <div style={{ background: 'var(--accent)', borderRadius: 10, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Zap size={18} color="white" fill="white" />
            </div>
          )}

          {/* Bouton fermer (X) — mobile seulement */}
          <button
            className="admin-sidebar-close btn btn-ghost btn-icon"
            onClick={() => setMobileOpen(false)}
            style={{ display: 'none' }}
          >
            <X size={18} />
          </button>

          {/* Bouton collapse — desktop seulement */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="btn btn-ghost btn-icon admin-menu-desktop"
          >
            {collapsed
              ? (isRTL ? <ChevronLeft size={16} /> : <ChevronRight size={16} />)
              : (isRTL ? <ChevronRight size={16} /> : <ChevronLeft size={16} />)
            }
          </button>
        </div>

        {/* ── Navigation ── */}
        <nav style={{ flex: 1, padding: '16px 10px', display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto' }}>
          {navItems.map(item => {
            const Icon = item.icon;
            const active = location.pathname === item.path ||
              (item.path !== '/admin' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                title={collapsed ? (item.label || t('admin', item.key)) : ''}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: collapsed ? '11px' : '11px 14px',
                  borderRadius: 12,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  background: active ? 'var(--accent-dim)' : 'transparent',
                  color: active ? 'var(--accent)' : 'var(--text-secondary)',
                  borderInlineStart: active ? '3px solid var(--accent)' : '3px solid transparent',
                  transition: 'var(--transition)',
                  fontWeight: active ? 700 : 500,
                  fontSize: 14,
                  textDecoration: 'none',
                }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; } }}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; } }}
              >
                <Icon size={18} strokeWidth={active ? 2.5 : 2} style={{ flexShrink: 0 }} />
                {!collapsed && <span>{item.label || t('admin', item.key)}</span>}
              </Link>
            );
          })}
        </nav>

        {/* ── Logout ── */}
        <div style={{ padding: '16px 10px', borderTop: '1.5px solid var(--border)' }}>
          <button
            onClick={handleLogout}
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: collapsed ? '11px' : '11px 14px',
              borderRadius: 12, width: '100%',
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--danger)',
              justifyContent: collapsed ? 'center' : 'flex-start',
              fontSize: 14, fontWeight: 500,
              transition: 'var(--transition)',
              fontFamily: 'var(--font-body)',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(244,63,94,0.1)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            <LogOut size={18} style={{ flexShrink: 0 }} />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
}